import { X, Award, Download } from 'lucide-react';
import { certificateService } from '../services/certificateService';

// Helper function to safely parse dates
const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return 'Not specified';
    
    try {
        // Handle different date formats
        let date: Date;
        if (dateString.includes('T')) {
            // Already has time component
            date = new Date(dateString);
        } else {
            // Date only, add time component
            date = new Date(dateString + 'T00:00:00');
        }
        
        // Check if date is valid
        if (isNaN(date.getTime())) {
            console.warn('Invalid date:', dateString);
            return 'Invalid date';
        }
        
        return date.toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
        });
    } catch (error) {
        console.error('Error parsing date:', dateString, error);
        return 'Invalid date';
    }
};

interface Certificate {
    id: string;
    courseId: string;
    title: string;
    courseTitle?: string;
    image?: string;
    issuedTo?: string;
    employeeId?: string;
    department?: string;
    employeeImage?: string;
    completedDate?: string;
    expiryDate?: string;
    issuedDate?: string;
    issuedAt?: string;
    issued_at?: string;
    dateEarned?: string;
}

interface User {
    name: string;
    avatarUrl?: string;
}

interface CertificateModalProps {
    isOpen: boolean;
    onClose: () => void;
    certificateData: Certificate | null;
    currentUser: User;
}

export function CertificateModal({ isOpen, onClose, certificateData, currentUser }: CertificateModalProps) {
    if (!isOpen || !certificateData) return null;

    const handleDownload = async () => {
        try {
            const { blob, filename } = await certificateService.downloadCertificate(certificateData.id);
            
            // Create download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading certificate:', error);
            alert('Failed to download certificate. Please try again later.');
        }
    };

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40 flex justify-center items-center p-4 transition-opacity duration-300 ease-out"
            onClick={onClose}
        >
            <div
                className="bg-white rounded-lg shadow-xl w-full max-w-2xl relative p-4 sm:p-6 transform transition-all duration-300 ease-out scale-95 opacity-0 animate-fade-scale-in"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    onClick={onClose}
                    className="absolute top-3 right-3 text-slate-400 hover:text-slate-600"
                    aria-label="Close modal"
                >
                    <X size={24} />
                </button>

                <h2 className="text-xl font-semibold text-slate-800 mb-4">Certificate of Completion</h2>

                <div 
                    id="certificate-content"
                    className="border-4 border-primary-600 p-6 md:p-8 bg-gradient-to-br from-primary-50 to-primary-100 rounded text-center relative"
                >
                    <div className="absolute top-0 left-0 w-12 h-12 border-t-2 border-l-2 border-primary-300 rounded-tl-md"></div>
                    <div className="absolute bottom-0 right-0 w-12 h-12 border-b-2 border-r-2 border-primary-300 rounded-br-md"></div>
                    <div className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-4">
                        <Award className="w-full h-full text-primary-500" />
                    </div>
                    <p className="text-sm text-slate-600 mb-2">This certifies that</p>
                    <p className="text-xl md:text-2xl font-bold text-slate-800 mb-4">{currentUser.name}</p>
                    <p className="text-sm text-slate-600 mb-2">has successfully completed the course</p>
                    <p className="text-lg md:text-xl font-semibold text-primary-700 mb-4">{certificateData.courseTitle || certificateData.title}</p>
                    <p className="text-sm text-slate-500">on {formatDate(certificateData.issued_at)}</p>
                    <div className="mt-6 text-xs text-slate-400">
                        Certificate ID: {certificateData.id} | Issued by CareLMS
                    </div>
                </div>

                <div className="mt-6 text-center">
                    <button
                        onClick={handleDownload}
                        className="flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 mx-auto"
                    >
                        <Download className="w-4 h-4 mr-2" />
                        Download PDF
                    </button>
                </div>
            </div>
            <style>{`
                @keyframes fade-scale-in {
                    from { opacity: 0; transform: scale(0.95); }
                    to { opacity: 1; transform: scale(1); }
                }
                .animate-fade-scale-in {
                    animation: fade-scale-in 0.3s ease-out forwards;
                }
            `}</style>
        </div>
    );
}